// src/services/postComposerService.js
// Save helpers for PostComposer, reusing existing store actions and upload utils

import { uploadMediaFile, uploadImageWithVariants, uploadMediaFileWithProgress } from "@/utils/uploadUtils";

const isBlobLike = (item) =>
  typeof Blob !== "undefined" && item instanceof Blob;

const isImageFile = (item) =>
  isBlobLike(item) && String(item.type || "").startsWith("image/");

const isVideoFile = (item) =>
  isBlobLike(item) && String(item.type || "").startsWith("video/");

const isVideoUrl = (url) =>
  typeof url === "string" && /(\.(mp4|webm|mov|m4v))(\?|$)/i.test(url);

const inferMediaType = (item) => {
  if (isVideoFile(item) || isVideoUrl(item)) return "video";
  return "image";
};

/**
 * Save a Photos post.
 * Inputs:
 * - caption: string
 * - filesOrUrls: Array<File|string>
 * - thumbnailIndex: number (index in filesOrUrls)
 * - owner: string (uid)
 * - organisation: string (orgId)
 * - store: Vuex store instance
 * Output: { id, collection: 'images' }
 */
export async function savePhotosPost({
  title = "",
  caption = "",
  description = "",
  imageCaptions = [],
  mediaThumbnails = [],
  mediaVariants = [],
  mediaTypes = [],
  filesOrUrls = [],
  thumbnailIndex = 0,
  owner,
  organisation,
  store,
  creationOperationId,
}) {
  if (!store) throw new Error("store is required");
  if (!owner || !organisation)
    throw new Error("owner and organisation are required");

  // Normalize and cap to 20
  const items = (filesOrUrls || []).slice(0, 20);
  if (!items.length && !caption.trim()) {
    throw new Error("Nothing to post");
  }

  const inferredMediaTypes = items.map(
    (item, index) => mediaTypes[index] || inferMediaType(item),
  );

  // Compute a stable order and effective thumbnail index after filtering
  const urls = [];
  const fileEntries = [];
  items.forEach((it, i) => {
    if (typeof it === "string") {
      urls.push({ url: it, origIndex: i });
    } else if (it instanceof File || it instanceof Blob) {
      fileEntries.push({ file: it, origIndex: i });
    }
  });

  // Determine which element is the thumbnail by original index
  const desiredThumbOrigIndex = Math.max(
    0,
    Math.min(thumbnailIndex, items.length - 1)
  );

  // Upload files preserving order. Only image files go through the responsive
  // variants path; video files must stay as video media entries.
  let variants = undefined;
  const byOrigIndex = new Map();
  const variantsByOrigIndex = new Map();
  urls.forEach((u) => byOrigIndex.set(u.origIndex, u.url));
  urls.forEach((u) => {
    const existingVariants = mediaVariants[u.origIndex];
    if (existingVariants && typeof existingVariants === "object") {
      variantsByOrigIndex.set(u.origIndex, existingVariants);
    }
  });

  for (const entry of fileEntries) {
    if (isImageFile(entry.file)) {
      const res = await uploadImageWithVariants(entry.file, {
        folder: "images",
        ownerUid: owner,
        widths: [256, 512, 1024, 1920],
      });
      byOrigIndex.set(entry.origIndex, res.url);
      variantsByOrigIndex.set(entry.origIndex, res.variants || {});
      if (entry.origIndex === desiredThumbOrigIndex) {
        variants = res.variants;
      }
      continue;
    }
    const url = await uploadMediaFile(entry.file, { ownerUid: owner });
    byOrigIndex.set(entry.origIndex, url);
  }

  // Combine preserving original order: place thumbUrl at its original position
  const orderedIndexes = Array.from({ length: items.length }, (_, i) => i);
  const images = [];
  const finalMediaTypes = [];
  const finalMediaThumbnails = [];
  const finalMediaVariants = [];
  orderedIndexes.forEach((idx) => {
    const url = byOrigIndex.get(idx);
    if (!url) return;
    images.push(url);
    finalMediaTypes.push(inferredMediaTypes[idx] || inferMediaType(url));
    finalMediaThumbnails.push(mediaThumbnails[idx] || "");
    finalMediaVariants.push(variantsByOrigIndex.get(idx) || null);
  });

  // Compute new thumbnail index in final images array
  const desiredThumbUrl = byOrigIndex.get(desiredThumbOrigIndex);
  const newThumbIndex = Math.max(0, images.indexOf(desiredThumbUrl));

  // Persist via existing action (creates images doc; itemsTimeline is handled by CF)
  const payload = {
    itemName: title?.trim() || "",
    caption: caption?.trim() || "",
    description: description?.trim() || "",
    imageCaptions: Array.isArray(imageCaptions) ? imageCaptions : [],
    images,
    mediaType: "image",
    owner,
    organisation,
    thumbnailIndex: newThumbIndex,
    creationOperationId,
  };
  if (variants && Object.keys(variants).length) payload.variants = variants;
  if (finalMediaTypes.some((type) => type && type !== "image")) {
    payload.mediaTypes = finalMediaTypes;
  }
  if (finalMediaThumbnails.some(Boolean)) {
    payload.mediaThumbnails = finalMediaThumbnails;
  }
  if (finalMediaVariants.some(Boolean)) {
    payload.mediaVariants = finalMediaVariants;
  }

  const res = await store.dispatch("mediaActions/addMediaPost", payload);
  return { id: res?.id, collection: "images" };
}

/**
 * Save a Photos post with progress tracking.
 * Integrates with uploadQueue store for global progress bar visibility.
 * @param {Object} params - Same as savePhotosPost plus onProgress callback
 * @returns {Promise<{id, collection}>}
 */
export async function savePhotosPostWithProgress({
  title = "",
  caption = "",
  description = "",
  imageCaptions = [],
  mediaThumbnails = [],
  mediaVariants = [],
  mediaTypes = [],
  filesOrUrls = [],
  thumbnailIndex = 0,
  owner,
  organisation,
  store,
  onProgress,
  uploadId,
  creationOperationId,
}) {
  if (!store) throw new Error("store is required");
  if (!owner || !organisation)
    throw new Error("owner and organisation are required");

  const shouldManageQueueLifecycle = !uploadId;

  // Start tracking in upload queue (unless caller provided an existing job)
  const effectiveUploadId =
    uploadId ||
    (await store.dispatch("uploadQueue/startUpload", {
      filename: title?.trim() || "Post",
    }));

  const reportProgress = (progress) => {
    const clamped = Math.max(0, Math.min(100, Math.round(progress)));
    store.dispatch("uploadQueue/updateProgress", {
      id: effectiveUploadId,
      progress: clamped,
    });
    if (typeof onProgress === "function") onProgress(clamped);
  };

  try {
    const items = (filesOrUrls || []).slice(0, 20);
    if (!items.length && !caption.trim()) {
      throw new Error("Nothing to post");
    }

    const inferredMediaTypes = items.map(
      (item, index) => mediaTypes[index] || inferMediaType(item),
    );

    const urls = [];
    const fileEntries = [];
    items.forEach((it, i) => {
      if (typeof it === "string") {
        urls.push({ url: it, origIndex: i });
      } else if (it instanceof File || it instanceof Blob) {
        fileEntries.push({ file: it, origIndex: i });
      }
    });

    const filesOnly = fileEntries.map((e) => e.file);
    const fileSizes = filesOnly.map((f) => (typeof f?.size === "number" ? f.size : 0));
    const totalBytes = fileSizes.reduce((sum, n) => sum + n, 0) || 1;
    const transferredBytesByIndex = new Array(filesOnly.length).fill(0);
    const reportBytesProgress = () => {
      const transferred = transferredBytesByIndex.reduce((sum, n) => sum + n, 0);
      reportProgress(Math.round((transferred / totalBytes) * 100));
    };

    const setFileProgress = (fileIndex, percent) => {
      const p = Math.max(0, Math.min(100, Number(percent) || 0));
      transferredBytesByIndex[fileIndex] = Math.round((fileSizes[fileIndex] || 0) * (p / 100));
      reportBytesProgress();
    };

    const markFileDone = (fileIndex) => {
      transferredBytesByIndex[fileIndex] = fileSizes[fileIndex] || 0;
      reportBytesProgress();
    };

    const desiredThumbOrigIndex = Math.max(0, Math.min(thumbnailIndex, items.length - 1));

    let variants = undefined;
    const byOrigIndex = new Map();
    const variantsByOrigIndex = new Map();
    urls.forEach((u) => byOrigIndex.set(u.origIndex, u.url));
    urls.forEach((u) => {
      const existingVariants = mediaVariants[u.origIndex];
      if (existingVariants && typeof existingVariants === "object") {
        variantsByOrigIndex.set(u.origIndex, existingVariants);
      }
    });

    // Upload remaining files with progress
    for (const entry of fileEntries) {
      const fileIndex = filesOnly.findIndex((f) => f === entry.file);
      if (isImageFile(entry.file)) {
        // Every image gets responsive delivery variants; the selected image is
        // also mirrored to the legacy primary `variants` field.
        const res = await uploadImageWithVariants(entry.file, {
          folder: "images",
          ownerUid: owner,
          widths: [256, 512, 1024, 1920],
        });
        byOrigIndex.set(entry.origIndex, res.url);
        variantsByOrigIndex.set(entry.origIndex, res.variants || {});
        if (entry.origIndex === desiredThumbOrigIndex) {
          variants = res.variants;
        }
        if (fileIndex >= 0) markFileDone(fileIndex);
        continue;
      }

      const url = await uploadMediaFileWithProgress(entry.file, {
        ownerUid: owner,
        onProgress: (p) => {
          if (fileIndex >= 0) setFileProgress(fileIndex, p);
        },
      });
      byOrigIndex.set(entry.origIndex, url);
      if (fileIndex >= 0) markFileDone(fileIndex);
    }

    // Combine preserving order
    const orderedIndexes = Array.from({ length: items.length }, (_, i) => i);
    const images = [];
    const finalMediaTypes = [];
    const finalMediaThumbnails = [];
    const finalMediaVariants = [];
    orderedIndexes.forEach((idx) => {
      const url = byOrigIndex.get(idx);
      if (!url) return;
      images.push(url);
      finalMediaTypes.push(inferredMediaTypes[idx] || inferMediaType(url));
      finalMediaThumbnails.push(mediaThumbnails[idx] || "");
      finalMediaVariants.push(variantsByOrigIndex.get(idx) || null);
    });
    const desiredThumbUrl = byOrigIndex.get(desiredThumbOrigIndex);
    const newThumbIndex = Math.max(0, images.indexOf(desiredThumbUrl));

    const payload = {
      itemName: title?.trim() || "",
      caption: caption?.trim() || "",
      description: description?.trim() || "",
      imageCaptions: Array.isArray(imageCaptions) ? imageCaptions : [],
      images,
      mediaType: "image",
      owner,
      organisation,
      thumbnailIndex: newThumbIndex,
      creationOperationId,
    };
    if (variants && Object.keys(variants).length) payload.variants = variants;
    if (finalMediaTypes.some((type) => type && type !== "image")) {
      payload.mediaTypes = finalMediaTypes;
    }
    if (finalMediaThumbnails.some(Boolean)) {
      payload.mediaThumbnails = finalMediaThumbnails;
    }
    if (finalMediaVariants.some(Boolean)) {
      payload.mediaVariants = finalMediaVariants;
    }

    const res = await store.dispatch("mediaActions/addMediaPost", payload);

    if (shouldManageQueueLifecycle) {
      store.dispatch("uploadQueue/finishUpload", { id: effectiveUploadId });
    }
    
    return { id: res?.id, collection: "images" };
  } catch (error) {
    if (shouldManageQueueLifecycle) {
      store.dispatch("uploadQueue/failUpload", {
        id: effectiveUploadId,
        error: error?.message || "Upload failed",
      });
    }
    throw error;
  }
}

/** Save a text-only post (title and/or caption, no media files). Supports optional embed fields for YouTube/SoundCloud links. */
export async function saveTextualPost({
  title = "",
  caption = "",
  description = "",
  owner,
  organisation,
  store,
  embedHtml = "",
  embedUrl = "",
  embedProvider = null,
  thumbnailUrl = "",
  embedIsVertical = false,
  embedIsSquare = false,
  creationOperationId,
}) {
  console.log("[saveTextualPost] called", { title, caption: caption?.substring(0, 40), embedHtml: embedHtml?.substring(0, 60), embedUrl, embedProvider });
  if (!store) throw new Error("store is required");
  if (!owner || !organisation)
    throw new Error("owner and organisation are required");

  const itemName = title?.trim() || "";
  const captionText = caption?.trim() || "";
  const descriptionText = description?.trim() || "";
  if (!itemName && !captionText && !descriptionText && !embedHtml) throw new Error("Nothing to post");

  const payload = {
    itemName,
    caption: captionText,
    description: descriptionText,
    images: [],
    // Embed posts are canonical media embeds; plain text remains a text post.
    // The authority contract must receive the final type because it does not
    // infer content from arbitrary browser fields.
    mediaType: embedHtml ? "embed" : "text",
    owner,
    organisation,
    thumbnailIndex: 0,
    creationOperationId,
  };

  // Attach embed fields if present (YouTube, SoundCloud links)
  if (embedHtml) {
    payload.embedHtml = embedHtml;
    payload.embedUrl = embedUrl || "";
    payload.embedProvider = embedProvider || null;
    payload.embedIsVertical = embedIsVertical;
    payload.embedIsSquare = embedIsSquare;
    if (thumbnailUrl) payload.thumbnailUrl = thumbnailUrl;
  }

  const res = await store.dispatch("mediaActions/addMediaPost", payload);
  return { id: res?.id, collection: "images" };
}

/**
 * Save an Audio post.
 * Inputs:
 * - title: string
 * - caption: string
 * - audioFile: File (the audio file)
 * - coverImage: File|string|null (optional cover image)
 * - owner: string (uid)
 * - organisation: string (orgId)
 * - store: Vuex store instance
 * - uploadId: optional existing uploadQueue job id for progress
 * Output: { id, collection: 'audios' }
 */
export async function saveAudioPost({
  title = "",
  caption = "",
  description = "",
  audioFile,
  coverImage = null,
  owner,
  organisation,
  store,
  uploadId,
  creationOperationId,
}) {
  if (!store) throw new Error("store is required");
  if (!owner || !organisation)
    throw new Error("owner and organisation are required");
  if (!audioFile) throw new Error("Audio file is required");

  const shouldManageQueueLifecycle = !uploadId;

  const effectiveUploadId =
    uploadId ||
    (await store.dispatch("uploadQueue/startUpload", {
      filename: title?.trim() || audioFile?.name || "Audio",
    }));

  const reportProgress = (progress) => {
    const clamped = Math.max(0, Math.min(100, Math.round(progress)));
    store.dispatch("uploadQueue/updateProgress", {
      id: effectiveUploadId,
      progress: clamped,
    });
  };

  try {
    // Calculate total bytes for progress
    const audioSize = audioFile?.size || 0;
    const coverSize =
      coverImage instanceof File || coverImage instanceof Blob
        ? coverImage.size || 0
        : 0;
    const totalBytes = audioSize + coverSize || 1;
    let transferredBytes = 0;

    // Upload audio file
    const { uploadMediaFileWithProgress } = await import("@/utils/uploadUtils.js");
    const audioUrl = await uploadMediaFileWithProgress(audioFile, {
      folder: "audios",
      ownerUid: owner,
      onProgress: (p) => {
        const audioTransferred = Math.round((audioSize * p) / 100);
        reportProgress(((audioTransferred) / totalBytes) * 100);
      },
    });
    transferredBytes = audioSize;
    reportProgress((transferredBytes / totalBytes) * 100);

    // Upload cover image if provided
    let coverUrl = null;
    if (coverImage) {
      if (typeof coverImage === "string") {
        coverUrl = coverImage;
      } else if (coverImage instanceof File || coverImage instanceof Blob) {
        const { uploadImageWithVariants } = await import("@/utils/uploadUtils.js");
        const res = await uploadImageWithVariants(coverImage, {
          folder: "images",
          ownerUid: owner,
          widths: [256, 512],
        });
        coverUrl = res?.url || null;
        transferredBytes += coverSize;
        reportProgress((transferredBytes / totalBytes) * 100);
      }
    }

    const payload = {
      itemName: title?.trim() || "",
      caption: caption?.trim() || "",
      description: description?.trim() || "",
      mediaType: "audio",
      audioUrl,
      images: coverUrl ? [coverUrl] : [],
      owner,
      organisation,
      thumbnailIndex: 0,
      creationOperationId,
    };

    const res = await store.dispatch("mediaActions/addMediaPost", payload);

    if (shouldManageQueueLifecycle) {
      store.dispatch("uploadQueue/finishUpload", { id: effectiveUploadId });
    }

    return { id: res?.id, collection: "audios" };
  } catch (error) {
    if (shouldManageQueueLifecycle) {
      store.dispatch("uploadQueue/failUpload", {
        id: effectiveUploadId,
        error: error?.message || "Upload failed",
      });
    }
    throw error;
  }
}
